import React, { useState } from 'react';
import { MessageCircle, BarChart3, History, Send, Settings } from 'lucide-react';
import { Member, Town, Event } from '../../types';
import { darkModeClasses, getInfoPanelClasses } from '../../utils/darkMode';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { SMSComposer } from './SMSComposer';
import { SMSAnalytics } from './SMSAnalytics.tsx';
import { SMSHistory } from './SMSHistory';
import { HelpButton } from '../Help/HelpButton';

interface SMSCenterProps {
  members: Member[];
  towns: Town[];
  selectedTownId: string;
  events: Event[];
}

export const SMSCenter: React.FC<SMSCenterProps> = ({
  members,
  towns,
  selectedTownId,
  events
}) => {
  const [activeTab, setActiveTab] = useState('compose');

  const selectedTownName = (() => {
    if (selectedTownId === 'no-town-assigned') {
      return 'No Town Assigned';
    }
    if (selectedTownId === 'all') {
      return 'All Towns';
    }
    return towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h1 className={`text-xl md:text-2xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <MessageCircle className="h-6 w-6 md:h-7 md:w-7 mr-2 md:mr-3 text-blue-600" />
            <span className="hidden sm:inline">SMS Center - </span>{selectedTownName}
          </h1>
          <div className="flex items-center gap-3">
            <div className={`text-sm ${darkModeClasses.textTertiary}`}>
              <Settings className="h-4 w-4 inline mr-1" />
              Configure SMS settings in Settings → SMS
            </div>
            <HelpButton guideId="sms/sms-configuration" variant="icon" />
          </div>
        </div>
        <p className={`${darkModeClasses.textSecondary} mt-1`}>
          Send bulk SMS to family members using Hubtel or NALO Solutions
        </p>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full grid-cols-3 bg-white dark:bg-gray-800`}>
          <TabsTrigger value="compose" className="flex items-center space-x-2">
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Compose</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <SMSComposer
            members={members}
            towns={towns}
            selectedTownId={selectedTownId}
            events={events}
          />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <SMSAnalytics selectedTownId={selectedTownId} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <SMSHistory selectedTownId={selectedTownId} limit={20} />
        </TabsContent>
      </Tabs>

      {/* Footer Info */}
      <div className={`${getInfoPanelClasses('yellow')} border rounded-lg p-4 mt-8`}>
        <h4 className="font-medium mb-2 flex items-center">
          <Settings className="h-4 w-4 mr-2" />
          SMS Provider Integration
        </h4>
        <p className="text-sm mb-2">
          Configure SMS settings and integrate with providers:
        </p>
        <ul className="text-sm space-y-1 ml-4">
          <li>• Hubtel SMS API (recommended for Ghana)</li>
          <li>• NALO Solutions (alternative provider)</li>
          <li>• Configure API keys in Settings → SMS Settings</li>
          <li>• Test SMS functionality before sending to members</li>
        </ul>
      </div>
    </div>
  );
};
